import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom'; // Import useParams to get the slug
import SidebarFilter from 'components/artectum/sidebar-filters/sidebar-filters';
import { useProducts } from 'core/hooks/useProducts';
import { useFilters } from 'core/hooks/useFilters';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import useFetch from 'core/hooks/api/useFetch';
import ProductGrid from './ProductGrid';
import Pagination from './Pagination';
import QuickViewModal from 'components/artectum/modal/QuickViewModal';
import Slider from 'react-slick';
import CategoryItem from '../cards/category-card';
import { categoriesSliderSettings } from 'core/config/slides';
import PlaceholderCategoryItem from '../cards/PlaceholderCategoryItem';
import PlaceholderCard from '../cards/PlaceholderCard';
import { useFiltersCards } from 'core/context/filtersCardsContext';
import { getData } from 'core/services/api'; // Import the API service
import { Helmet } from 'react-helmet';
import { searchProducts } from 'core/services/api';

const formatCategoryValue = (value) => {
    return value
        .replace(/[-_]/g, ' ')
        .replace(/\b\p{L}+/gu, word => 
            word.charAt(0).toUpperCase() + 
            word.slice(1).toLowerCase()
        );
};

const GridProduct = ({ customerClass }) => {
    const location = useLocation();
    const { slug } = useParams();
    const [selectedFilters, setSelectedFilters] = useState({});
    const [productsData, setProductsData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState({});
    const { data: productsFilter, loading: loadingProductsFilters } = useFetch(API_ENDPOINTS.PRODUCT_FILTERS);
    const { data: filter, loading: loadingFilters } = useFetch(API_ENDPOINTS.FILTERS);
    const { getProductCategories, registerProductCategories } = useFiltersCards();
    const productCategories = getProductCategories();
    const { data: fetchedProductCategories, error: productCategoriesError } = useFetch(
        productCategories ? null : API_ENDPOINTS.FILTER + "/search/home_category"
    );

    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (!productCategories && fetchedProductCategories) {
            registerProductCategories(fetchedProductCategories);
        }
    }, [productCategories, fetchedProductCategories, registerProductCategories]);

    const { currentProducts, currentPage, pageCount, handlePageChange, setCurrentPage } = useProducts(productsData, setSelectedFilters);

    const validFilters = useMemo(() => 
        filter
            ?.filter(f => productsFilter?.some(pf => pf.filter_id === f.id))
            ?.filter(f => f.name !== 'project_filters' && f.name !== 'product_filters')
            ?.filter(f => f.name !== 'home_category' && f.name !== 'home_category'),
        [filter, productsFilter]
    );

    const { filters, selectedCategory, setSelectedCategory, handleFilterChange, setFilters, resetFilters } = useFilters(validFilters);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const handleQuickView = (product) => {
        setSelectedProduct(product);
        setShowModal(true);
    };

    const handleModalClose = () => setShowModal(false);

    const searchStats = {
        resultCount: productsData ? productsData.total : 0,
        searchTime: 0.62
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (params.has('query')) {
            const query = params.get('query');
            setSearchQuery(query);
            handleSearch(query);
            return; // Salir temprano si es una búsqueda
        }

        setSearchQuery('');

        const initialFilters = {};
        params.forEach((value, key) => {
            // Excluir parámetros que contengan 'utm'
            if (!key.toLowerCase().includes('utm')) {
                initialFilters[`filters[${key}]`] = value;
            }
        });

        setSelectedFilters(initialFilters);

        const updatedFilters = validFilters?.map(f => {
            const filterKey = `filters[${f.name.toLowerCase()}]`;
            if (initialFilters[filterKey]) {
                const values = initialFilters[filterKey].split(',');
                return {
                    ...f,
                    options: f.options?.map(option => ({
                        ...option,
                        checked: values.includes(option.value)
                    })),
                    defaultOpen: true
                };
            }
            return f;
        }) || [];

        setFilters(updatedFilters);

        const activeFiltersCount = Object.keys(initialFilters || {}).length;
        setSelectedCategory(activeFiltersCount > 0 && selectedCategory === "" ? null : selectedCategory);

        // Estos valores especiales sí deben aplicarse
        if (params.has('product_navbar')) {
            const navbarValue = params.get('product_navbar');
            setSelectedCategory(navbarValue);
            initialFilters['filters[product_navbar]'] = navbarValue;
        }

        if (params.has('home_category')) {
            const homeCategoryValue = params.get('home_category');
            setSelectedCategory(homeCategoryValue);
            initialFilters['filters[home_category]'] = homeCategoryValue;
        }

        console.debug('Initial filters from URL (limpios):', initialFilters);

        fetchProducts(initialFilters);
    }, [location.search, validFilters]);


    const handleSearch = async (query) => {
        setIsLoading(true);
        try {
            const response = await searchProducts(query);
            setProductsData({
                data: response.data,
                total: response.data.length // Ajusta según la estructura de tu respuesta
            });
            // Resetear otros estados relevantes
            setSelectedFilters({});
            setSelectedCategory('');
            resetFilters();
        } catch (error) {
            console.error('Error en la búsqueda:', error);
            setError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterChangeWithRefetch = (sectionTitle, value, checked, type) => {
        setSearchQuery('');
        handleFilterChange(sectionTitle, value, checked);
        setSelectedFilters(prevFilters => {
            const newFilters = { ...prevFilters };
            const filterKey = `filters[${sectionTitle.toLowerCase()}]`;
            const currentValues = newFilters[filterKey] ? newFilters[filterKey].split(',') : [];

            if (type === 'single') {
                if (checked) {
                    newFilters[filterKey] = value;
                } else {
                    delete newFilters[filterKey];
                }
            } else {
                if (checked) {
                    currentValues.push(value);
                } else {
                    const index = currentValues.indexOf(value);
                    if (index > -1) {
                        currentValues.splice(index, 1);
                    }
                }

                if (currentValues.length > 0) {
                    newFilters[filterKey] = currentValues.join(',');
                } else {
                    delete newFilters[filterKey];
                }
            }

            // Reset pagination to page 1
            newFilters.page = 1;
            setCurrentPage(1);

            // Trigger fetch with updated filters
            fetchProducts(newFilters);
            return newFilters;
        });
    };

    const handleClearFilters = () => {
        setSelectedFilters({});
        resetFilters(); // Reset all filters in the sidebar
        setSelectedCategory(''); // Reset selectedCategory to an empty string
        const params = new URLSearchParams(location.search);
        params.forEach((_, key) => {
            params.delete(key);
        });
        window.history.replaceState({}, '', `${location.pathname}`);
    };

    const handleCategorySelect = (categoryValue) => {
        setSearchQuery(''); // Clear search query when selecting a category
        if (categoryValue === 'Todos los productos') {
            setSelectedCategory(''); // Reset to default category
            const newFilters = {};
            setSelectedFilters(newFilters);
            resetFilters(); // Reset all filters in the sidebar
            setCurrentPage(1); // Reset pagination to page 1
            fetchProducts(newFilters); // Trigger fetch without filters
        } else {
            const formattedCategoryValue = categoryValue.toLowerCase().replace(/\s+/g, '_');
            setSelectedCategory(formattedCategoryValue);
            setSelectedFilters(prevFilters => {
                const newFilters = {
                    ...prevFilters,
                    'filters[home_category]': formattedCategoryValue,
                    page: 1 // Reset pagination to page 1
                };
                setCurrentPage(1); // Reset pagination to page 1
                fetchProducts(newFilters); // Trigger fetch with updated filters
                return newFilters;
            });
        }
    };

    // Update fetchProducts to accept filters as a parameter
    const fetchProducts = async (filters) => {
        setIsLoading(true);
        try {
            const endpoint = `${API_ENDPOINTS.PRODUCT_PAGINATED}`;
            const response = Object.keys(filters)?.length > 0
                ? await getData(endpoint, filters)
                : await getData(endpoint);
            setProductsData(response.data);
        } catch (error) {
            console.error('Error fetching products:', error);
            setError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const seoMeta = useMemo(() => {
        switch (slug) {
            case 'celosias-de-concreto':
                return {
                    title: 'Celosías de Concreto en Lima | Diseños Personalizados',
                    description: 'Celosías de concreto decorativas para arquitectura moderna. Personalización y resistencia para cualquier proyecto. Cotiza aquí.'
                };
            case 'celosías-de-concreto':
                return {
                    title: 'Celosías de Concreto en Lima | Diseños Personalizados',
                    description: 'Celosías de concreto decorativas para arquitectura moderna. Personalización y resistencia para cualquier proyecto. Cotiza aquí.'
                };
            case 'baldosas-de-terrazo':
                return {
                    title: 'Baldosas de Terrazo en Lima | Resistentes y Elegantes',
                    description: 'Baldosas de terrazo con alta resistencia y diseño personalizado para espacios interiores y exteriores. Cotiza con Artectum.'
                };
            case 'mosaicos-artesanales':
                return {
                    title: 'Mosaicos Artesanales en Perú | Diseños Únicos',
                    description: 'Mosaicos decorativos y artesanales para pisos y paredes. Diseños exclusivos y duraderos. Pide una cotización.'
                };
            case 'pisos-podotactiles':
                return {
                    title: 'Pisos Podotáctiles en Lima | Seguridad y Normativa',
                    description: 'Pisos podotáctiles para accesibilidad y seguridad en espacios públicos y privados. Consulta precios y modelos.'
                };
            default:
                return {
                    title: 'Baldosas, Terrazos y Mosaicos en Lima | Artectum',
                    description: 'Encuentra baldosas, mosaicos, celosías y terrazos de concreto de alta calidad para proyectos residenciales y comerciales. Cotiza con nosotros.'
                };
        }
    }, [slug]);

    return (
        <div className={`ltn__product-area ltn__product-gutter ${customerClass}`}>
            <Helmet>
                <title>{seoMeta.title}</title>
                <meta name="description" content={seoMeta.description} />
            </Helmet>
            <div className="container">
                <div className="row mt-5" style={{ position: 'relative' }}>
                    <div className="col-lg-9 order-lg-2 mb-100" style={{ minHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>
                        <div className="ltn__shop-options mb-0 bg-white p-0">
                            <div className="col-12 mb-2 p-0">
                                <h6 className="fw-bold mb-3 pt-3">¿Qué estas buscando?</h6>
                                <Slider {...categoriesSliderSettings} className="slick-arrow-5 hidden-mob">
                                    <CategoryItem
                                        key="all-products"
                                        category={{ value: 'Todos los productos', icon: 'fas fa-th-large' }}
                                        isSelected={selectedCategory === ''}
                                        onSelect={handleCategorySelect}
                                    />
                                    {productCategories ? (
                                        productCategories[0]?.options?.map((category) => (
                                            <CategoryItem
                                                key={category.id}
                                                category={{ ...category, value: formatCategoryValue(category.value) }}
                                                isSelected={selectedCategory === category.value}
                                                onSelect={handleCategorySelect}
                                            />
                                        ))
                                    ) : (
                                        [...Array(7)].map((_, index) => (
                                            <div className="col-md-3 col-sm-6 col-6" key={index}>
                                                <PlaceholderCategoryItem />
                                            </div>
                                        ))
                                    )}
                                </Slider>
                            </div>
                        </div>

                        <div className="tab-content">
                            <div className="tab-pane fade active show" id="liton_product_grid">
                                <div className="ltn__product-tab-content-inner ltn__product-grid-view">
                                    {isLoading ? (
                                        <div className="row d-flex justify-content-center">
                                            {[...Array(4)].map((_, index) => (
                                                <div className="col-md-3 col-sm-6 col-6" key={index}>
                                                    <PlaceholderCard />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <ProductGrid products={currentProducts} onQuickView={handleQuickView} />
                                    )}
                                </div>
                            </div>
                        </div>

                        { !searchQuery && (
                            <Pagination
                                currentPage={currentPage}
                                pageCount={pageCount}
                                onPageChange={(page) => {
                                    handlePageChange(page); // Update page state
                                    fetchProducts({ ...selectedFilters, page }); // Trigger fetch with updated page
                                }}
                            />
                        )}
                    </div>
                    <SidebarFilter
                        filters={filters}
                        searchStats={searchStats}
                        loading={loadingProductsFilters || loadingFilters}
                        onFilterChange={(sectionTitle, value, checked) => handleFilterChangeWithRefetch(sectionTitle, value, checked, filters.find(f => f.name === sectionTitle).type)}
                        title="Filtros de Búsqueda"
                        columnSize="col-lg-3"
                        onClearFilters={handleClearFilters} // Pasar handleClearFilters
                    />
                </div>
            </div>
            {selectedProduct && (
                <QuickViewModal
                    show={showModal}
                    product={selectedProduct}
                    onHide={handleModalClose}
                    item={selectedProduct}
                />
            )}
        </div>
    );
};

export default GridProduct;